import type { App, AsyncComponentLoader } from 'vue'

import type { Plugin } from './plugin'
import { usePlugin } from './plugin'
import { createUnique } from './utils'

export type CustomElement = {
  id: string
  component: AsyncComponentLoader
  plugins?: Plugin[]
  configureApp?: (args: { app: App }) => Promise<void>
  configureInstance?: (args: { app: App }) => Promise<void>
  styles?: string[]
  shadowRoot?: boolean
  nonce?: string
}

export const createCustomElement = (customElement: CustomElement) =>
  createUnique('custom element', customElement.id, customElement)

export const customElementFactory = async (element: CustomElement, _plugins: Plugin[]) => {
  // merge global and local plugins
  const plugins = [
    ..._plugins, // global plugins
    ...(element.plugins || []) // local plugins
  ]

  // load all required pieces at once
  const [{ defineCustomElement, nextTick }, component, ...vuePlugins] = await Promise.all([
    import('vue'),
    element.component(),
    ...plugins.map((plugin) => plugin.load())
  ])

  customElements.define(
    element.id,
    defineCustomElement(component.default || component, {
      styles: element.styles,
      shadowRoot: element.shadowRoot,
      nonce: element.nonce,
      configureApp: async (app: App) => {
        vuePlugins.forEach((plugin) => usePlugin(app, plugin))
        await element.configureApp?.({ app })

        await app.runWithContext(async () => {
          await nextTick()
          await element.configureInstance?.({ app })
        })
      }
    })
  )
}

export const buildCustomElementFactories = (
  customElements: CustomElement[],
  plugins: Plugin[]
): Array<() => void> => {
  return customElements.map((customElement) =>
    customElementFactory.bind(null, customElement, plugins)
  )
}
